import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, CheckCircle2, XCircle, Clock, RefreshCw, ExternalLink,
  Loader2, Crown, Star, FileText, Send, Download, Copy,
} from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";

// Phase 25.11 — super-admin queue for analyst applications.

const STATUS_TABS = [
  { id: "pending",  label: "PENDING",  icon: Clock },
  { id: "approved", label: "APPROVED", icon: CheckCircle2 },
  { id: "rejected", label: "REJECTED", icon: XCircle },
];

export default function AdminAnalysts() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/analysts?status=${tab}`);
      setRows(r.data.analysts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (a, tier) => {
    setBusy(a.id);
    try {
      const r = await api.post(`/admin/analysts/${a.id}/approve`, { tier });
      toast.success(`Approved · code ${r.data.code} (${tier})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (a) => {
    const reason = window.prompt("Rejection reason (optional, shared with applicant):", "");
    if (reason === null) return; // cancelled
    setBusy(a.id);
    try {
      await api.post(`/admin/analysts/${a.id}/reject`, { reason });
      toast.success("Application rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await api.post("/admin/analysts/recompute");
      toast.success(`Recomputed earnings for ${r.data.analysts_updated} analysts`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6" data-testid="admin-analysts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">ADMIN · ANALYSTS</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1">Analyst Queue</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            Review applicants, assign tier on approval, and recompute earnings nightly.
          </p>
        </div>
        <button
          onClick={recompute}
          disabled={recomputing}
          data-testid="admin-analysts-recompute"
          className="border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000] px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${recomputing ? "animate-spin" : ""}`} />
          {recomputing ? "RECOMPUTING…" : "RECOMPUTE EARNINGS"}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-900 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
        <div className="flex items-center gap-1 sm:gap-2 min-w-max sm:min-w-0">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-analysts-tab-${t.id}`}
              className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-widest px-3 sm:px-4 py-3 border-b-2 transition-colors inline-flex items-center gap-2 ${
                tab === t.id ? "text-[#FFB000] border-[#FFB000]" : "text-zinc-500 border-transparent hover:text-white"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="font-mono text-xs text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Panel title={`// NO ${tab.toUpperCase()} APPLICATIONS`}>
          <div className="p-6 text-center font-mono text-xs text-zinc-500">
            Empty queue. Applicants land here from /experts/apply.
          </div>
        </Panel>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => (
            <AnalystRow
              key={a.id}
              a={a}
              tab={tab}
              busy={busy === a.id}
              onApprove={approve}
              onReject={reject}
              onReload={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnalystRow({ a, tab, busy, onApprove, onReject, onReload }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const sendAgreement = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/analysts/${a.id}/send-agreement`);
      const url = r.data?.signing_url;
      if (url) {
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        toast.success(
          r.data.email_delivered
            ? "Agreement emailed. Signing URL copied to clipboard too."
            : "Email not delivered (Resend key missing). Signing URL copied to clipboard — send manually.",
          { duration: 8000 }
        );
      } else {
        toast.success("Agreement sent.");
      }
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send agreement.");
    } finally {
      setSending(false);
    }
  };

  const downloadSigned = async () => {
    try {
      const res = await api.get(`/admin/analysts/${a.id}/signed-agreement`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `dara-agreement-signed-${a.code || a.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download.");
    }
  };

  const isSigned = !!a.signature_at;
  const isSent = !!a.agreement_sent_at;
  return (
    <div className="border border-zinc-900 bg-zinc-950/40" data-testid={`analyst-row-${a.id}`}>
      <div className="p-4 sm:p-5 flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-base sm:text-lg leading-tight">{a.name}</h3>
            {a.tier === "senior" && a.status === "approved" && (
              <span className="px-2 py-0.5 border border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000] font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1">
                <Crown className="w-3 h-3" /> SENIOR
              </span>
            )}
            {a.code && (
              <span className="font-mono text-xs text-[#FFB000]">{a.code}</span>
            )}
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>{a.email}</span>
            <span>· {a.country}</span>
            {a.timezone && <span>· {a.timezone}</span>}
            <span>· {a.years_experience}y exp</span>
            <span>· wants {a.desired_tier?.toUpperCase()}</span>
          </div>
          {a.industries?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {a.industries.map((i) => (
                <span key={i} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-800 text-zinc-400">
                  {i}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          {a.linkedin && (
            <a
              href={a.linkedin}
              target="_blank"
              rel="noreferrer"
              className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:text-[#FFB000] hover:border-[#FFB000]/50 inline-flex items-center gap-1.5"
            >
              <ExternalLink className="w-3 h-3" /> LINKEDIN
            </a>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:text-white"
          >
            {open ? "HIDE BIO" : "READ BIO"}
          </button>
        </div>
      </div>

      {open && a.bio && (
        <div className="px-5 pb-4 -mt-1">
          <div className="border-l-2 border-[#FFB000]/40 pl-3 py-2 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
            {a.bio}
          </div>
        </div>
      )}

      {tab === "pending" && (
        <div className="px-5 pb-5 flex flex-wrap gap-2">
          <button
            onClick={() => onApprove(a, "standard")}
            disabled={busy}
            data-testid={`analyst-approve-standard-${a.id}`}
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            APPROVE · STANDARD
          </button>
          <button
            onClick={() => onApprove(a, "senior")}
            disabled={busy}
            data-testid={`analyst-approve-senior-${a.id}`}
            className="border border-[#FFB000] text-[#FFB000] hover:bg-[#FFB000]/10 font-mono text-[11px] uppercase tracking-widest px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Crown className="w-3.5 h-3.5" />
            APPROVE · SENIOR
          </button>
          <button
            onClick={() => onReject(a)}
            disabled={busy}
            data-testid={`analyst-reject-${a.id}`}
            className="border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 font-mono text-[11px] uppercase tracking-widest px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <XCircle className="w-3.5 h-3.5" />
            REJECT
          </button>
        </div>
      )}

      {tab === "approved" && (
        <>
          <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-zinc-900 pt-4 mt-2">
            <Stat label="LIFETIME $" value={`$${(a.earnings_lifetime_usd || 0).toFixed(2)}`} />
            <Stat label="UNPAID $" value={`$${(a.earnings_unpaid_usd || 0).toFixed(2)}`} accent="#FFB000" />
            <Stat label="REFERRALS" value={`${a.referrals_count || 0}`} />
            <Stat label="ACTIVE NOW" value={`${a.referrals_active || 0}`} />
          </div>
          <div className="px-5 pb-5 -mt-2 flex flex-wrap items-center gap-2" data-testid={`analyst-agreement-actions-${a.id}`}>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-1">
              AGREEMENT:
            </span>
            {isSigned ? (
              <>
                <span
                  data-testid={`analyst-agreement-status-${a.id}`}
                  className="px-2 py-1 border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676] font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  SIGNED · {new Date(a.signature_at).toLocaleDateString()}
                </span>
                <button
                  onClick={downloadSigned}
                  data-testid={`analyst-download-signed-${a.id}`}
                  className="border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                >
                  <Download className="w-3 h-3" />
                  DOWNLOAD SIGNED
                </button>
              </>
            ) : isSent ? (
              <>
                <span
                  data-testid={`analyst-agreement-status-${a.id}`}
                  className="px-2 py-1 border border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000] font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                >
                  <Clock className="w-3 h-3" />
                  AWAITING SIGNATURE
                </span>
                <button
                  onClick={sendAgreement}
                  disabled={sending}
                  data-testid={`analyst-resend-agreement-${a.id}`}
                  className="border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {sending ? "SENDING…" : "RESEND"}
                </button>
              </>
            ) : (
              <button
                onClick={sendAgreement}
                disabled={sending}
                data-testid={`analyst-send-agreement-${a.id}`}
                className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {sending ? "SENDING…" : "SEND AGREEMENT"}
              </button>
            )}
          </div>
        </>
      )}

      {tab === "rejected" && a.rejection_reason && (
        <div className="px-5 pb-5 font-mono text-[10px] text-zinc-500">
          REASON: <span className="text-zinc-300">{a.rejection_reason}</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "#FFFFFF" }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-base mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
